import { useMemo } from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from "recharts";

interface DataPoint {
  points: number;
  created_at: string;
  type?: string;
}

interface PointsChartProps {
  history: DataPoint[];
  height?: number;
}

const DAYS = 60;

const fmtDate = (d: Date) => `${d.getMonth() + 1}/${d.getDate()}`;

const toDateKey = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/**
 * Cumulative points area chart for the last 60 days.
 * Zero is centred on the Y axis; positive area is green, negative is red.
 */
export function PointsChart({ history, height = 200 }: PointsChartProps) {
  const { data, domain } = useMemo(() => {
    /* ---- aggregate daily point changes ---- */
    const dailyDelta = new Map<string, number>();
    if (history) {
      for (const h of history) {
        const d = new Date(h.created_at);
        const key = toDateKey(d);
        dailyDelta.set(key, (dailyDelta.get(key) || 0) + h.points);
      }
    }

    /* ---- build 60-day series, filling gaps with 0 delta ---- */
    const now = new Date();
    const data: { date: string; fullDate: string; points: number }[] = [];
    let cumulative = 0;

    // Sum up any history that happened before the 60-day window so the
    // starting cumulative value is correct.
    const windowStart = new Date(now);
    windowStart.setDate(windowStart.getDate() - (DAYS - 1));
    windowStart.setHours(0, 0, 0, 0);

    if (history) {
      for (const h of history) {
        if (new Date(h.created_at) < windowStart) {
          cumulative += h.points;
        }
      }
    }

    for (let i = DAYS - 1; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      d.setHours(0, 0, 0, 0);
      const key = toDateKey(d);
      cumulative += dailyDelta.get(key) || 0;
      data.push({
        date: fmtDate(d),
        fullDate: d.toLocaleDateString(),
        points: cumulative,
      });
    }

    /* ---- symmetric Y domain centred on 0 ---- */
    const maxVal = Math.max(...data.map((d) => d.points), 0);
    const minVal = Math.min(...data.map((d) => d.points), 0);
    const absMax = Math.max(Math.abs(maxVal), Math.abs(minVal), 1);
    const domain: [number, number] = [-absMax, absMax];

    return { data, domain };
  }, [history]);

  // Recharts chart area: top margin=10, X-axis labels ≈30 px.
  // Since domain is symmetric [-absMax, absMax], y=0 sits at the exact midpoint.
  const MARGIN_TOP = 10;
  const X_AXIS_HEIGHT = 30;
  const zeroY = MARGIN_TOP + (height - MARGIN_TOP - X_AXIS_HEIGHT) / 2;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 10, right: 10, bottom: 0, left: -10 }}>
        <defs>
          {/* Fill gradient: green above zero → transparent → transparent → red below zero.
               gradientUnits="userSpaceOnUse" means y1/y2 are SVG pixel coords so the
               colour boundary always coincides with the actual y=0 chart line. */}
          <linearGradient id="pointsFillGrad" x1="0" y1={0} x2="0" y2={height} gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#22c55e" stopOpacity={0.35} />
            <stop offset={zeroY / height} stopColor="#22c55e" stopOpacity={0.05} />
            <stop offset={zeroY / height} stopColor="#ef4444" stopOpacity={0.05} />
            <stop offset="100%" stopColor="#ef4444" stopOpacity={0.35} />
          </linearGradient>

          {/* Stroke gradient: green at top, red at bottom, hard switch at zero */}
          <linearGradient id="pointsStrokeGrad" x1="0" y1={0} x2="0" y2={height} gradientUnits="userSpaceOnUse">
            <stop offset={zeroY / height} stopColor="#22c55e" />
            <stop offset={zeroY / height} stopColor="#ef4444" />
          </linearGradient>
        </defs>

        <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.1} vertical={false} />

        <XAxis
          dataKey="date"
          tick={{ fontSize: 11, fill: "currentColor", opacity: 0.5 }}
          axisLine={false}
          tickLine={false}
          minTickGap={40}
        />

        <YAxis
          domain={domain}
          tick={{ fontSize: 11, fill: "currentColor", opacity: 0.5 }}
          axisLine={false}
          tickLine={false}
          width={36}
          allowDataOverflow
        />

        <Tooltip
          contentStyle={{
            background: "var(--color-app-surface-raised)",
            border: "1px solid var(--color-app-border)",
            borderRadius: 8,
            fontSize: 12,
          }}
          labelFormatter={(_label, payload) => payload?.[0]?.payload?.fullDate ?? _label}
          formatter={(value: number | undefined) => {
            const v = value ?? 0;
            const color = v >= 0 ? "#22c55e" : "#ef4444";
            return [<span style={{ color }}>{v}</span>, "Points"];
          }}
        />

        <ReferenceLine
          y={0}
          stroke="currentColor"
          strokeOpacity={0.15}
          strokeDasharray="4 2"
        />

        <Area
          type="monotone"
          dataKey="points"
          stroke="url(#pointsStrokeGrad)"
          strokeWidth={2}
          fill="url(#pointsFillGrad)"
          dot={false}
          activeDot={{ r: 4 }}
          animationDuration={600}
          baseValue={0}
          isAnimationActive={true}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}
